/**
 * Wire protocol used between the host (lum-web iframe / React Native WebView)
 * and this forms application.
 *
 * The protocol is intentionally tiny — one message type per concern — so it
 * is trivial to keep both sides in sync. Bump `BRIDGE_VERSION` on any
 * breaking change so the host can refuse to talk to an incompatible build.
 */

export const BRIDGE_VERSION = 1 as const;

/** Locales currently supported by lum-web. */
export type Locale = 'en' | 'es' | 'zh';

/** Locales we ship catalogs for. */
export const SUPPORTED_LOCALES: readonly Locale[] = ['en', 'es', 'zh'];

/**
 * Normalise a locale that arrived over the wire. `init.locale` / `locale:update`
 * are typed `Locale`, but they're postMessage input the type system can't
 * enforce — a host may send `'en_US'`, `'en-US'` or junk. We take the primary
 * subtag (`'en_US'` / `'en-US'` → `'en'`) and fall back to `'en'` for anything
 * unsupported, so downstream `Intl` and catalog lookups never get a tag that
 * would throw (`new Intl.NumberFormat('en_US')` throws `RangeError`).
 */
export const sanitizeLocale = (raw: unknown): Locale => {
  if (typeof raw !== 'string') return 'en';
  const primary = raw.toLowerCase().split(/[-_]/)[0];
  return (SUPPORTED_LOCALES as readonly string[]).includes(primary)
    ? (primary as Locale)
    : 'en';
};

/**
 * Theme tokens. Shape matches the `colors.json` files that lum-web loads
 * per tenant (e.g. `src/styles/theme/lum/colors.json`). The host owns the
 * source of truth and passes the resolved set on `init` / `theme:update`.
 */
export type ThemeTokens = Record<string, string | number>;

/* ------------------------------------------------------------------ */
/* Host → Forms                                                       */
/* ------------------------------------------------------------------ */

export type InitMessage = {
  type: 'init';
  v: typeof BRIDGE_VERSION;
  token: string;
  tenant: string;
  locale: Locale;
  theme: ThemeTokens;
  flowId: string;
  /** Optional step to land on. Falls back to the flow's default first step. */
  entryStep?: string;
  /** Arbitrary payload available to the flow (e.g. pre-filled values). */
  input?: Record<string, unknown>;
  /**
   * Optional deployment-context headers (e.g. `x-customerid`, `marketplacekey`,
   * `x-solution`). The host computes these from its own configuration and JWT
   * claims; `createApiClient` merges them into every outgoing request. Flow
   * code never names these headers — they're invisible at the call site.
   */
  headers?: Record<string, string>;
};

export type TokenUpdateMessage = { type: 'token:update'; token: string };
export type ThemeUpdateMessage = { type: 'theme:update'; theme: ThemeTokens };
export type LocaleUpdateMessage = { type: 'locale:update'; locale: Locale };

/** Host asks the form to jump to a specific step. */
export type GotoMessage = { type: 'goto'; stepId: string };

/** Host signals "user pressed back" (e.g. Android hardware button). */
export type BackRequestMessage = { type: 'back:request' };

export type InboundMessage =
  | InitMessage
  | TokenUpdateMessage
  | ThemeUpdateMessage
  | LocaleUpdateMessage
  | GotoMessage
  | BackRequestMessage;

/* ------------------------------------------------------------------ */
/* Forms → Host                                                       */
/* ------------------------------------------------------------------ */

/** Forms app has mounted and is ready to receive `init`. */
export type ReadyMessage = { type: 'ready'; v: typeof BRIDGE_VERSION };

/**
 * Forms app has first-paintable content on screen — i.e. past any internal
 * loading/bootstrap state. The host uses this to hide its overlay spinner
 * without revealing intermediate loading shimmers from inside the iframe.
 * Idempotent: hosts treat the first occurrence as the signal and ignore
 * subsequent ones for the same mount.
 */
export type FlowReadyMessage = { type: 'flow:ready'; flowId: string };

/**
 * Reports the content height so the host iframe can size itself.
 * No-op for React Native WebView (use flex/100% there).
 */
export type ResizeMessage = { type: 'resize'; height: number };

/** Emitted on every step transition so the host can update analytics/URL. */
export type StepChangeMessage = {
  type: 'step:change';
  flowId: string;
  stepId: string;
  stepIndex: number;
  totalSteps: number;
};

/**
 * Emitted whenever the flow's intra-flow route changes (including the initial
 * one). Carries the path relative to the flow root — e.g. `'pending'` or
 * `'approve-payment/abc123'`, no leading slash. The host mirrors it into its
 * own URL bar so master→detail flows are bookmarkable/shareable and deep-links
 * reload the right screen. Distinct from `step:change` (which is an analytics
 * step id, not a URL).
 */
export type RouteChangeMessage = {
  type: 'route:change';
  flowId: string;
  path: string;
};

export type SubmitSuccessMessage = {
  type: 'submit:success';
  flowId: string;
  result: unknown;
};

export type SubmitErrorMessage = {
  type: 'submit:error';
  flowId: string;
  error: string;
};

export type CancelMessage = { type: 'cancel'; flowId: string };

/** Flow wants the host to navigate to a native screen mid-flow. */
export type NavigateNativeMessage = {
  type: 'navigate:native';
  route: string;
  params?: Record<string, unknown>;
};

export type TelemetryMessage = {
  type: 'telemetry';
  event: string;
  props?: Record<string, unknown>;
};

export type OutboundMessage =
  | ReadyMessage
  | FlowReadyMessage
  | ResizeMessage
  | StepChangeMessage
  | RouteChangeMessage
  | SubmitSuccessMessage
  | SubmitErrorMessage
  | CancelMessage
  | NavigateNativeMessage
  | TelemetryMessage;
